// @flow
import config from '../config';
import Toast from './toastService';
import I18n from './i18n';
import {reportError} from './analytics';
import WizzeeLog from './wizzeeLog';

type APIErrors = TypeError | Response | Error;
type DefaultHandler = (error: ?APIErrors) => ?string;
type NativeEvent = {description?: string, domain?: string};

export default class HandleErrorService {
  static isNoNetworkError(
    error: Error | TypeError | Response | {nativeEvent: NativeEvent},
  ): boolean {
    return (
      ((error instanceof Error || error instanceof TypeError) &&
        (error.message === 'Network request failed' || // react native
          error.message === 'Failed to fetch' || // chrome
          error.message === 'Type error' || // safari
          error.message ===
            'NetworkError when attempting to fetch resource.')) || // firefox
      // $FlowFixMe
      (!!error.nativeEvent &&
        (error.nativeEvent.domain === 'NSURLErrorDomain' || // iOS
          // $FlowFixMe
          error.nativeEvent.description === 'net::ERR_ADDRESS_UNREACHABLE')) // Android
    );
  }
  static isNoBackConnectionError(error: Response): boolean {
    return !error.status;
  }

  static isServerUnreachableError(error: TypeError | Response): boolean {
    // $FlowFixMe
    return (
      HandleErrorService.isNoNetworkError(error) ||
      HandleErrorService.isNoBackConnectionError(error)
    );
  }

  static sendDefault(
    error: ?APIErrors,
    defaultHandler: ?(error: ?APIErrors) => ?string,
  ) {
    const defaultMessage = I18n.t('error.default');
    if (!defaultHandler) {
      return defaultMessage;
    }
    return defaultHandler(error) || defaultMessage;
  }

  static getErrorMessage(error: ?APIErrors, defaultHandler: ?DefaultHandler) {
    const translateError = text => I18n.t(`error.${text}`);
    WizzeeLog.log('HandleErrorService.getErrorMessage', {error, defaultHandler});
    if (!error) {
      return HandleErrorService.sendDefault(error, defaultHandler);
    }
    
    // $FlowFixMe
    if (HandleErrorService.isNoNetworkError(error)) {
      WizzeeLog.log('HandleErrorService.getErrorMessage - noNetwork error detected', error);
      return translateError('noNetwork');
    } else if (error instanceof TypeError || error instanceof Error) {
      if (config.areCheatCodesEnabled) {
        WizzeeLog.log('HandleErrorService.getErrorMessage - areCheatCodesEnabled', error);
        return error.message;
      }
      WizzeeLog.log('HandleErrorService.getErrorMessage - sendDefault', error, defaultHandler);
      return HandleErrorService.sendDefault(error, defaultHandler);
    } else if (HandleErrorService.isNoBackConnectionError(error)) {
      return translateError('unableToJoinServer');
    } else if (error.status >= 500) {
      return translateError('error500');
    }
    return HandleErrorService.sendDefault(error, defaultHandler);
  }

  static showToastError(
    error: ?(APIErrors | string),
    defaultHandler: ?DefaultHandler,
  ) {
    // Always report the raw error object to Crashlytics so the full details
    // are captured regardless of which user-facing message is shown.
    if (error != null && typeof error !== 'string') {
      reportError(error);
    }
    Toast.showError(
      typeof error === 'string'
        ? error
        : HandleErrorService.getErrorMessage(error, defaultHandler),
    );
  }
}
